import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useUserAuth } from "context/UserContext";
import { studioApi } from "services/studioService";
import { errorText, formatDate, formatDay, formatPlanPrice, tierLabel } from "./studioText";
// The Studio styles (scoped under .studio), so StudioHostingPanel also looks
// right outside the Studio pages, in Account settings.
import "pages/studio/studio.css";

/* The Publish tab, in the order things happen:
     1. the app's home (its own Firebase project), set up once the app is on
        a paid plan (after the first message, or with "Set it up now")
     2. the Studio plan: which plan, and how many of its live apps are in use
     3. Publish puts the current version on the live address, if the app is
        live already or a live slot is free
   A custom domain is connected by Codenec by hand for now.

   Hosting is part of the Studio plans and is no longer sold per app. An app
   that still has its own hosting plan from before (legacy) keeps it here,
   manage-only: it can be cancelled and resumed, and it may publish on its
   own until it ends. */

const SETUP_STEPS = [
  "choose_id", "create_project", "link_billing", "enable_services", "add_firebase", "web_app",
  "firestore_default", "firestore_preview", "rules", "hosting_site", "preview_channel", "auth", "budget",
];
const PAID = new Set(["active", "trialing"]);
const LAPSED = new Set(["past_due", "unpaid", "incomplete", "paused"]);
const ENDED = new Set(["none", "canceled", "incomplete_expired"]);
const REASON_ID = "studio-publish-reason";

// Live, or holding a live slot (older documents have no liveSlot).
export const isLive = (project) => project?.liveSlot === true || Boolean(project?.liveUrl && !project?.liveDisabled);

function SetupProgress({ t, project }) {
  if (project.status === "ready") return <p className="studio-muted">{t("studio.publish.setupDone")}</p>;
  if (project.status === "draft") return <p className="studio-muted">{t("studio.publish.setupNotStarted")}</p>;
  const at = Math.max(0, SETUP_STEPS.indexOf(project.provisioning?.step));
  const phases = [
    { key: "project", until: 4 },
    { key: "firebase", until: 6 },
    { key: "database", until: 9 },
    { key: "hosting", until: 11 },
    { key: "signin", until: 13 },
  ];
  return (
    <>
      {project.status === "error" && (
        <div className="studio-notice">
          <p>{t(`studio.publish.setupErrors.${project.provisioning?.errorCode || "retrying"}`, { defaultValue: project.provisioning?.error })}</p>
        </div>
      )}
      <ul className="studio-steps-list">
        {phases.map((phase, i) => {
          const from = i === 0 ? 0 : phases[i - 1].until;
          const state = at >= phase.until ? "done" : at >= from ? "now" : "todo";
          return <li key={phase.key} className={`studio-step-${state}`}>{t(`studio.publish.setup.${phase.key}`)}</li>;
        })}
      </ul>
    </>
  );
}

/* A legacy hosting plan, manage-only: when it renews, Cancel with a
   confirmation step in ANY state (a plan whose payment failed is cancelled at
   once), and Resume for a cancellation the client made. No purchase: new
   hosting plans are not sold. `manageOnly` is the Account settings view,
   which leaves out the link back to Account settings. */
function HostingSection({ t, locale, user, project, onChanged, working, setWorking, setError, manageOnly = false }) {
  const [plan, setPlan] = useState(null);
  const [planError, setPlanError] = useState(null);
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const [notice, setNotice] = useState(null);
  const cancelQuestionRef = useRef(null);

  const projectId = project.id;
  const hostingStatus = project.hosting?.status;

  // Read through a ref so the plan reloads only when the app or its hosting
  // status changes, not whenever the user object or `t` gets a new identity.
  // A reload racing a just-finished cancel could otherwise show stale state.
  const userRef = useRef(user);
  userRef.current = user;

  const loadPlan = useCallback(async () => {
    setPlanError(null);
    try {
      setPlan(await studioApi.hostingPlan(userRef.current, projectId));
    } catch (err) {
      setPlanError(err);
    }
  }, [projectId]);

  useEffect(() => {
    loadPlan();
  }, [loadPlan, hostingStatus]);

  // Move focus to the question (not to either answer) so keyboard and
  // screen-reader users land in the confirmation with nothing pre-selected.
  useEffect(() => {
    if (confirmingCancel) cancelQuestionRef.current?.focus();
  }, [confirmingCancel]);

  const status = plan?.status ?? hostingStatus ?? "none";
  const paid = PAID.has(status);
  const lapsed = LAPSED.has(status);
  const price = plan?.price ? formatPlanPrice(plan.price, locale) : "";
  const billingLink = <a href="/accountsettings?tab=billing">{t("studio.publish.hostingCardLink")}</a>;

  const cancelHosting = async () => {
    setWorking("cancel");
    setError(null);
    try {
      const updated = await studioApi.hostingCancel(user, projectId);
      setPlan(updated);
      setConfirmingCancel(false);
      setNotice(t("studio.publish.hostingCanceledNow"));
      onChanged();
    } catch (err) {
      setError(errorText(t, err));
    } finally {
      setWorking(null);
    }
  };

  const resumeHosting = async () => {
    setWorking("resume");
    setError(null);
    try {
      setPlan(await studioApi.hostingResume(user, projectId));
      setNotice(t("studio.publish.hostingResumedNow"));
      onChanged();
    } catch (err) {
      setError(errorText(t, err));
    } finally {
      setWorking(null);
    }
  };

  const noticeBox = notice && (
    <div className="studio-notice" role="status" style={{ marginBottom: 10 }}><p>{notice}</p></div>
  );

  const cancelConfirm = (question) => (
    <div className="studio-notice" role="group" aria-labelledby={`hosting-cancel-question-${projectId}`} style={{ marginTop: 10 }}>
      <p id={`hosting-cancel-question-${projectId}`} ref={cancelQuestionRef} tabIndex={-1}>{question}</p>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button type="button" className="studio-btn studio-btn-primary" onClick={cancelHosting} disabled={working !== null}>
          {working === "cancel" ? t("studio.publish.hostingCanceling") : t("studio.publish.hostingCancelYes")}
        </button>
        <button type="button" className="studio-btn" onClick={() => setConfirmingCancel(false)} disabled={working !== null}>
          {t("studio.publish.hostingCancelNo")}
        </button>
      </div>
    </div>
  );

  if (paid) {
    const date = formatDay(plan?.renewsAt, locale);
    const ending = Boolean(plan?.cancelAtPeriodEnd);
    return (
      <>
        <p>
          <span className="studio-chip studio-chip-done">
            {ending ? t("studio.publish.hostingEnding") : t("studio.publish.hostingActive")}
          </span>
        </p>
        {noticeBox}
        {planError && !plan && <p className="studio-muted">{t("studio.publish.hostingPlanUnavailable")}</p>}
        {plan && !ending && date && (
          <p className="studio-muted">
            {price ? t("studio.publish.hostingRenewsOn", { date, price }) : t("studio.publish.hostingRenewsOnNoPrice", { date })}
          </p>
        )}
        {ending && (
          <>
            <p className="studio-muted">
              {date ? t("studio.publish.hostingCancelsOn", { date }) : t("studio.publish.hostingCancelsSoon")}
            </p>
            {plan?.canResume && (
              <button type="button" className="studio-btn" onClick={resumeHosting} disabled={working !== null}>
                {working === "resume" ? t("studio.publish.saving") : t("studio.publish.hostingResume")}
              </button>
            )}
          </>
        )}
        {/* Shown even if the plan details failed to load: cancelling only needs the app. */}
        {!ending && !confirmingCancel && (
          <button type="button" className="studio-btn" onClick={() => setConfirmingCancel(true)} disabled={working !== null}>
            {t("studio.publish.hostingCancel")}
          </button>
        )}
        {confirmingCancel &&
          cancelConfirm(date ? t("studio.publish.hostingCancelConfirm", { date }) : t("studio.publish.hostingCancelConfirmNoDate"))}
        {!manageOnly && (
          <p className="studio-muted" style={{ marginTop: 10 }}>
            {t("studio.publish.hostingCardHelp")} {billingLink}
          </p>
        )}
      </>
    );
  }

  if (lapsed) {
    // A plan exists but its payment failed: update the card, or cancel right away.
    return (
      <>
        {noticeBox}
        <div className="studio-notice" style={{ marginBottom: 10 }}>
          <p>{t("studio.publish.hostingPastDue")} {billingLink}</p>
        </div>
        {!confirmingCancel && (
          <button type="button" className="studio-btn" onClick={() => setConfirmingCancel(true)} disabled={working !== null}>
            {t("studio.publish.hostingCancel")}
          </button>
        )}
        {confirmingCancel && cancelConfirm(t("studio.publish.hostingCancelConfirmNow"))}
      </>
    );
  }

  // Nothing billing any more (for example, just cancelled here).
  return (
    <>
      {noticeBox}
      {!notice && <p className="studio-muted">{t("studio.publish.hostingNoPlan")}</p>}
    </>
  );
}

// Step 2: the plan this app publishes under.
function PlanSummary({ t, plan }) {
  if (!plan) return null;
  const line = t("studio.publish.planLine", {
    plan: tierLabel(t, plan.tier),
    used: plan.liveUsed ?? 0,
    total: plan.limits?.liveApps ?? 0,
  });
  if (plan.source === "comp" && plan.canPublish) {
    return (
      <>
        <p>{line}</p>
        <p className="studio-muted">{t("studio.plan.comp")}</p>
      </>
    );
  }
  if (plan.canPublish) {
    return (
      <p>
        {line} <Link to="/accountsettings?tab=billing">{t("studio.publish.planLink")}</Link>
      </p>
    );
  }
  return (
    <p className="studio-muted">
      {t("studio.publish.needsPlan")} <Link to="/studio/plans">{t("studio.publish.seePlans")}</Link>
    </p>
  );
}

const PAUSED_KEYS = {
  owner: "livePausedOwner",
  lapsed: "livePausedLapsed",
  downgrade: "livePausedDowngrade",
  traffic: "livePausedTraffic",
};

function domainText(t, plan, legacyPaid) {
  if (legacyPaid || !plan) return t("studio.publish.domainHelp");
  if (!plan.canPublish) return t("studio.publish.domainNone");
  if (plan.limits?.customDomains === "per_app") return t("studio.publish.domainPerApp");
  if (plan.limits?.customDomains === "one") return t("studio.publish.domainOne");
  return t("studio.publish.domainNone");
}

/**
 * `plan` is the Studio's PlanView with liveUsed (GET /studio/me), or null
 * while it loads. `onPlanChanged` reloads it after a slot is freed or used.
 */
function StudioPublish({ project, plan = null, onChanged, onPlanChanged = () => {} }) {
  const { t, i18n } = useTranslation();
  const { user } = useUserAuth();
  const [working, setWorking] = useState(null);
  const [error, setError] = useState(null);
  const [published, setPublished] = useState(null);
  const [notice, setNotice] = useState(null);
  // The other live apps, offered to take offline when every slot is in use.
  const [liveList, setLiveList] = useState(null);
  const [limitHit, setLimitHit] = useState(false);

  const locale = (i18n.language || "en").slice(0, 2) === "es" ? "es" : "en";
  const legacyStatus = project.hosting?.status ?? "none";
  const legacyPaid = PAID.has(legacyStatus);
  const hasLegacy = !ENDED.has(legacyStatus);
  const ready = project.status === "ready";
  const newer = project.publishedVersion && project.publishedVersion < project.currentVersion;

  const liveNow = isLive(project);
  const liveApps = plan?.limits?.liveApps ?? 0;
  const slotFree = Boolean(plan?.canPublish) && (plan?.liveUsed ?? 0) < liveApps;
  // With no plan loaded yet, the Studio decides.
  const allowed = legacyPaid || !plan || (plan.canPublish && (liveNow || slotFree));
  const full = Boolean(plan?.canPublish) && !legacyPaid && !liveNow && !slotFree;
  const reason = !allowed && !full ? "needsPlan" : !ready ? "waitSetup" : full || limitHit ? "full" : null;
  const publishOff = !ready || !allowed || working !== null;

  const userRef = useRef(user);
  userRef.current = user;

  // Every live slot is in use: list the apps holding them.
  useEffect(() => {
    if (!full) return undefined;
    let gone = false;
    studioApi
      .listProjects(userRef.current)
      .then(({ projects }) => {
        if (gone) return;
        setLiveList((projects || []).filter((p) => p.id !== project.id && isLive(p)).map((p) => ({ id: p.id, name: p.name })));
      })
      .catch(() => {});
    return () => {
      gone = true;
    };
  }, [full, project.id]);

  const publish = async () => {
    if (publishOff) return;
    setWorking("publish");
    setError(null);
    setNotice(null);
    try {
      setPublished(await studioApi.publish(user, project.id));
      setLimitHit(false);
      onChanged();
      onPlanChanged();
    } catch (err) {
      if (err?.code === "live_app_limit") {
        setLimitHit(true);
        setLiveList(Array.isArray(err.details?.live) ? err.details.live : []);
        onPlanChanged();
      } else {
        setError(errorText(t, err));
      }
    } finally {
      setWorking(null);
    }
  };

  const takeOffline = async (app) => {
    setWorking(`offline-${app.id}`);
    setError(null);
    try {
      await studioApi.unpublish(user, app.id);
      setLiveList((list) => (list || []).filter((p) => p.id !== app.id));
      setLimitHit(false);
      setNotice(t("studio.publish.takenOffline", { name: app.name }));
      onPlanChanged();
    } catch (err) {
      setError(errorText(t, err));
    } finally {
      setWorking(null);
    }
  };

  const setupNow = async () => {
    setWorking("setup");
    setError(null);
    try {
      await studioApi.setup(user, project.id);
      onChanged();
    } catch (err) {
      setError(errorText(t, err));
    } finally {
      setWorking(null);
    }
  };

  const pausedKey = PAUSED_KEYS[project.liveDisabledReason] || "livePaused";
  const quickOnly = plan && !plan.canCreateGoogleProject && !legacyPaid;

  return (
    <div className="studio-scroll studio-publish">
      {error && <div className="studio-notice" role="alert" style={{ marginBottom: 16 }}><p>{error}</p></div>}

      <section>
        <h3>{t("studio.publish.setupTitle")}</h3>
        {project.status === "draft" && quickOnly ? (
          <p className="studio-muted">{t("studio.publish.trialSetup")}</p>
        ) : (
          <SetupProgress t={t} project={project} />
        )}
        {project.status === "draft" && plan?.canCreateGoogleProject && (
          <button type="button" className="studio-btn" onClick={setupNow} disabled={working !== null}>
            {working === "setup" ? t("studio.publish.settingUpNow") : t("studio.publish.setupNow")}
          </button>
        )}
      </section>

      <section>
        <h3>{t("studio.publish.planTitle")}</h3>
        <PlanSummary t={t} plan={plan} />
        {hasLegacy && (
          <>
            <p className="studio-muted">{t("studio.publish.hostingLegacy")}</p>
            <HostingSection
              t={t}
              locale={locale}
              user={user}
              project={project}
              onChanged={onChanged}
              working={working}
              setWorking={setWorking}
              setError={setError}
            />
          </>
        )}
      </section>

      <section>
        <h3>{t("studio.publish.liveTitle")}</h3>
        {project.liveUrl ? (
          <p>
            <a href={project.liveUrl} target="_blank" rel="noopener noreferrer">{project.liveUrl}</a>{" "}
            <span className="studio-muted">
              {t("studio.publish.liveVersion", { n: project.publishedVersion, date: formatDate(project.publishedAt, i18n.language) })}
            </span>
          </p>
        ) : (
          <p className="studio-muted">{t("studio.publish.notLive")}</p>
        )}
        {project.liveDisabled && (
          <div className="studio-notice" style={{ marginBottom: 10 }}><p>{t(`studio.publish.${pausedKey}`)}</p></div>
        )}
        {project.retention?.deleteAt && !legacyPaid && (
          <div className="studio-notice" style={{ marginBottom: 10 }}>
            <p>{t("studio.publish.retention", { date: formatDay(project.retention.deleteAt, i18n.language) })}</p>
          </div>
        )}
        {newer && <p className="studio-muted">{t("studio.publish.newerChanges", { n: project.currentVersion })}</p>}
        {published && <div className="studio-notice" style={{ marginBottom: 10 }}><p>{t("studio.publish.publishedNow", { n: published.publishedVersion })}</p></div>}
        {notice && <div className="studio-notice" role="status" style={{ marginBottom: 10 }}><p>{notice}</p></div>}
        <button
          type="button"
          className="studio-btn studio-btn-primary"
          onClick={publish}
          aria-disabled={publishOff ? "true" : undefined}
          aria-describedby={reason ? REASON_ID : undefined}
        >
          {working === "publish" ? t("studio.publish.publishing") : t("studio.publish.publish", { n: project.currentVersion })}
        </button>
        {reason === "needsPlan" && (
          <p id={REASON_ID} className="studio-muted" style={{ marginTop: 8 }}>
            {t("studio.publish.needsPlan")} <Link to="/studio/plans">{t("studio.publish.seePlans")}</Link>
          </p>
        )}
        {reason === "waitSetup" && <p id={REASON_ID} className="studio-muted" style={{ marginTop: 8 }}>{t("studio.publish.waitSetup")}</p>}
        {reason === "full" && (
          <div id={REASON_ID} className="studio-notice" style={{ marginTop: 10 }}>
            <p>{plan?.tier === "starter" ? t("studio.publish.liveFull") : t("studio.publish.liveFullNoUpgrade")}</p>
            {liveList?.length > 0 && (
              <>
                <p><strong>{t("studio.publish.liveAppsTitle")}</strong></p>
                <ul className="studio-live-list">
                  {liveList.map((app) => (
                    <li key={app.id}>
                      <button type="button" className="studio-btn studio-btn-small" onClick={() => takeOffline(app)} disabled={working !== null}>
                        {working === `offline-${app.id}` ? t("studio.publish.takingOffline") : t("studio.publish.takeOffline", { name: app.name })}
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            )}
            {plan?.tier === "starter" && (
              <p><Link to="/accountsettings?tab=billing">{t("studio.publish.upgradeBusiness")}</Link></p>
            )}
          </div>
        )}
      </section>

      <section>
        <h3>{t("studio.publish.domainTitle")}</h3>
        <p className="studio-muted">{domainText(t, plan, legacyPaid)}</p>
      </section>
    </div>
  );
}

/**
 * One app's legacy hosting plan, for Account settings → Subscription: the
 * same view and Cancel flow as the Publish tab, loaded from the main backend
 * only, so it works even when the client's Studio access is off.
 */
export function StudioHostingPanel({ projectId, hostingStatus }) {
  const { t, i18n } = useTranslation();
  const { user } = useUserAuth();
  const [working, setWorking] = useState(null);
  const [error, setError] = useState(null);
  const locale = (i18n.language || "en").slice(0, 2) === "es" ? "es" : "en";

  return (
    <div className="studio studio-publish">
      {error && <div className="studio-notice" style={{ marginBottom: 10 }}><p>{error}</p></div>}
      <HostingSection
        t={t}
        locale={locale}
        user={user}
        project={{ id: projectId, hosting: { status: hostingStatus } }}
        onChanged={() => {}}
        working={working}
        setWorking={setWorking}
        setError={setError}
        manageOnly
      />
    </div>
  );
}

export default StudioPublish;
